import React, { useEffect, useState } from "react";
import { DeleteIconButton } from "components/DatatablesActionIcons";

export type OrderDiscountCodeFieldProps = {
  value: string;
  appliedCode?: string;
  discountAmount?: number;
  /** Parent confirms voucher is applied (keeps success UI even if Formik lags). */
  voucherApplied?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  onChange: (code: string) => void;
  onApply: (code: string) => void;
  onClear: () => void;
};

/**
 * Shared discount-code UI — matches existing CouponDiscount / Installation coupon design:
 * 1) Collapsed: blue-outline "Apply Discount Code" button
 * 2) Expanded: input + Apply + full-width Cancel
 */
export default function OrderDiscountCodeField({
  value,
  appliedCode = "",
  discountAmount = 0,
  voucherApplied = false,
  disabled = false,
  error = "",
  className = "",
  onChange,
  onApply,
  onClear,
}: OrderDiscountCodeFieldProps) {
  const [openPanel, setOpenPanel] = useState(false);
  const [voucherDismissed, setVoucherDismissed] = useState(false);
  const attemptedCode = String(
    error.trim() ? value || appliedCode : appliedCode || value
  ).trim();
  const hasApplied =
    !error.trim() &&
    Boolean(appliedCode.trim()) &&
    (Number(discountAmount) > 0 || Boolean(voucherApplied));
  const showApplied = hasApplied && !voucherDismissed;
  const showInvalidAttempt =
    Boolean(error.trim()) && Boolean(attemptedCode) && !showApplied;

  useEffect(() => {
    if (hasApplied) {
      setVoucherDismissed(false);
      setOpenPanel(false);
    }
  }, [hasApplied, appliedCode]);

  const handleApply = () => {
    const code = value.trim();
    if (!code || disabled) return;
    setOpenPanel(false);
    onApply(code);
  };

  const handleCancel = () => {
    setOpenPanel(false);
    if (!hasApplied && !showInvalidAttempt) {
      onChange("");
    }
  };

  const handleClear = () => {
    setVoucherDismissed(true);
    setOpenPanel(false);
    onChange("");
    onClear();
  };

  if (showInvalidAttempt) {
    return (
      <div className={className}>
        <div className="alert alert-success d-flex justify-content-between align-items-center mb-0 py-2 px-3">
          <div>
            <strong>Discount Code:</strong> {attemptedCode}
            <div className="text-danger small mt-1" role="alert">
              {error}
            </div>
          </div>
          <DeleteIconButton
            type="button"
            onClick={handleClear}
            disabled={disabled}
            title="Remove"
          />
        </div>
      </div>
    );
  }

  if (showApplied) {
    return (
      <div className={className}>
        <div className="alert alert-success d-flex justify-content-between align-items-center mb-0 py-2 px-3">
          <div>
            <strong>Voucher Applied:</strong> {appliedCode}
            <div className="small text-muted">
              {Number(discountAmount) > 0
                ? `-$${Number(discountAmount).toFixed(2)} discount`
                : "Discount applied"}
            </div>
          </div>
          <DeleteIconButton
            type="button"
            onClick={handleClear}
            disabled={disabled}
            title="Remove"
          />
        </div>
        {error ? <div className="text-danger small mt-2">{error}</div> : null}
      </div>
    );
  }

  if (!openPanel) {
    return (
      <div className={className}>
        <style>{`
          .order-discount-toggle-btn {
            font-size: 14px;
            font-weight: 600;
            color: #155dfc !important;
            border: 1px solid #155dfc !important;
            background-color: #fff !important;
          }
          .order-discount-toggle-btn:hover:not(:disabled),
          .order-discount-toggle-btn:focus:not(:disabled) {
            background-color: #155dfc !important;
            color: #fff !important;
            border-color: #155dfc !important;
            box-shadow: none;
          }
          .order-discount-toggle-btn:hover:not(:disabled) i {
            color: #fff;
          }
          .order-discount-toggle-btn i {
            color: #155dfc;
          }
        `}</style>
        <button
          type="button"
          className="btn w-100 br-8 d-flex align-items-center justify-content-center order-discount-toggle-btn"
          onClick={() => setOpenPanel(true)}
          disabled={disabled}
          style={{ padding: "10px 14px" }}
        >
          <i className="ri-price-tag-3-line me-2" />
          Apply Discount Code
        </button>
        {error ? <div className="text-danger small mt-2">{error}</div> : null}
      </div>
    );
  }

  return (
    <div className={className}>
      <style>{`
        .order-discount-apply-btn {
          background-color: #a0cfff !important;
          color: #1f2937 !important;
          font-size: 14px;
          font-weight: 600;
          border: 1px solid #7eb8f5 !important;
          white-space: nowrap;
        }
        .order-discount-apply-btn:hover:not(:disabled),
        .order-discount-apply-btn:focus:not(:disabled) {
          background-color: #7eb8f5 !important;
          border-color: #5a9fe0 !important;
          color: #111827 !important;
          font-weight: 600;
          box-shadow: none;
        }
        .order-discount-apply-btn:disabled {
          opacity: 0.65;
          background-color: #a0cfff !important;
          border-color: #7eb8f5 !important;
        }
        .order-discount-cancel-btn {
          background: #f1f3f5;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          border: 1px solid #e5e7eb;
        }
        .order-discount-cancel-btn:hover:not(:disabled) {
          background: #e9ebef;
          font-weight: 600;
          border-color: #d1d5db;
        }
      `}</style>
      <div className="border rounded-3 p-3 bg-white">
        <div className="d-flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter discount code"
            disabled={disabled}
            className="form-control br-8"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
          />
          <button
            type="button"
            className="btn br-8 px-3 order-discount-apply-btn"
            onClick={handleApply}
            disabled={disabled || !value.trim()}
          >
            {disabled ? "Applying..." : "Apply"}
          </button>
        </div>

        {error ? (
          <div className="text-danger small mt-2" role="alert">
            {error}
          </div>
        ) : null}

        <button
          type="button"
          className="btn w-100 mt-3 br-8 order-discount-cancel-btn"
          onClick={handleCancel}
          disabled={disabled}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
